import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import type { ResourceTypeHandle } from '@toolproof-core/genesis/_contract/_index';
import { resolveRuntimeController } from '@toolproof-core/visualization/adapter/mode-controller-delegation';
import type { CosmosData } from '@toolproof-core/visualization/derivation/_contract/derivation';
import type { SpaceOverlayIntent } from '@toolproof-core/visualization/runtime/_contract/overlay';

import type { SpaceAdapterDescriptor } from '@/_contract/types';
import type { SessionMode } from '@/drivers/nativeAdapterDriver/_contract/session';
import {
	withRuntimeStateChangeCallback,
	type RuntimeCallbackCarrier,
} from '@/drivers/nativeAdapterDriver/_pure/withRuntimeStateChangeCallback';

export function useNativeAdapterSession<
	TResourceTypeHandle extends ResourceTypeHandle,
	TCosmosData extends CosmosData<TResourceTypeHandle>,
	TRuntimeState,
	TConfig extends RuntimeCallbackCarrier<TRuntimeState>,
>(args: {
	descriptor: SpaceAdapterDescriptor<TResourceTypeHandle, TCosmosData, TConfig, TRuntimeState>;
	cosmosData: TCosmosData;
	loading: boolean;
	episodeHandle?: string;
	episodeSoundDefault?: boolean;
	arrivalOrigin?: string;
	stopRuntimeEffects: () => void;
	navigateToEpisode: (handle?: string) => void;
}) {
	const { descriptor, cosmosData, loading, episodeHandle, stopRuntimeEffects, navigateToEpisode } = args;
	const resolvedConfig = useMemo(() => ({
		...descriptor.baseDescriptor.defaultConfig,
		episodeSoundDefault: args.episodeSoundDefault ?? false,
		...(args.arrivalOrigin ? { arrivalOrigin: args.arrivalOrigin } : {}),
	}), [descriptor.baseDescriptor.defaultConfig, args.episodeSoundDefault, args.arrivalOrigin]);
	const initialRuntimeController = useMemo(
		() => resolveRuntimeController({
			descriptor,
			config: resolvedConfig,
			runtimeState: undefined,
		}),
		[descriptor, resolvedConfig],
	);
	const [runtimeState, setRuntimeState] = useState<TRuntimeState>(() =>
		initialRuntimeController.createInitialRuntimeState({ config: resolvedConfig }),
	);
	const runtimeConfig = useMemo(() => {
		return withRuntimeStateChangeCallback<TRuntimeState, TConfig>(resolvedConfig, (updateRuntimeState) => {
			setRuntimeState(updateRuntimeState);
		});
	}, [resolvedConfig]);

	const presentation = descriptor.episodePresentation;
	const [choosingEpisode, setChoosingEpisode] = useState(false);
	const [routeError, setRouteError] = useState<string>();
	const [exploration, setExploration] = useState<{
		handle: string; checkpoint: TRuntimeState; detached: TRuntimeState; confirmRestore: boolean;
	}>();
	const [appliedEpisode, setAppliedEpisode] = useState<{ handle?: string; data?: TCosmosData; config?: TConfig }>();
	const [playbackActive, setPlaybackActive] = useState(false);
	const [animationSuspended, setAnimationSuspended] = useState(false);
	const [freeCamera, setFreeCameraState] = useState(false);
	const animationSuspendedRef = useRef(false);
	const [playbackCheckpoint, setPlaybackCheckpoint] = useState<{ runtimeState: TRuntimeState }>();
	const suspendedCompletionRef = useRef(false);
	const [playbackResetKey, setPlaybackResetKey] = useState(0);
	const playbackGenerationRef = useRef(0);
	useLayoutEffect(() => {
		playbackGenerationRef.current = playbackResetKey;
		animationSuspendedRef.current = animationSuspended;
	}, [playbackResetKey, animationSuspended]);
	useLayoutEffect(() => {
		suspendedCompletionRef.current = false;
	}, [playbackResetKey]);

	// Reconcile before commit so the previous session cannot reach SpaceRuntime.
	if (!loading && (!appliedEpisode || appliedEpisode.handle !== episodeHandle
		|| (routeError && (appliedEpisode.data !== cosmosData || appliedEpisode.config !== runtimeConfig)))) {
		let nextState = runtimeState;
		let failureMessage: string | undefined;
		try {
			if (!episodeHandle) {
				if (appliedEpisode?.handle) nextState = presentation?.detach(runtimeState) ?? runtimeState;
			}
			else {
				if (!presentation) throw new Error('Episodes are not supported by this space.');
				nextState = presentation.enter({ config: runtimeConfig, runtimeState,
					cosmosData, handle: episodeHandle });
			}
		} catch (failure) {
			failureMessage = (failure instanceof Error ? failure.message : String(failure)) || 'Episode entry failed.';
		}
		setAppliedEpisode(failureMessage
			? { handle: episodeHandle, data: cosmosData, config: runtimeConfig }
			: { handle: episodeHandle });
		setRuntimeState(nextState);
		setExploration(undefined);
		setChoosingEpisode(false);
		setRouteError(failureMessage);
		setPlaybackResetKey(playbackResetKey + 1);
		setAnimationSuspended(false);
		setPlaybackCheckpoint(undefined);
		setFreeCameraState(false);
	}
	useEffect(() => {
		if (routeError) console.error('[NativeAdapterDriver] Episode entry failed:', routeError);
	}, [routeError]);

	// Preserve derivation invalidation on operational updates, even while the rendered state is frozen.
	const derivationState = useMemo(
		() => ({ runtimeState: playbackCheckpoint?.runtimeState ?? runtimeState }),
		[runtimeState, playbackCheckpoint],
	);
	const runtimeController = useMemo(
		() => resolveRuntimeController({
			descriptor,
			config: runtimeConfig,
			runtimeState,
		}),
		[descriptor, runtimeConfig, runtimeState],
	);
	const mode: SessionMode = episodeHandle && !exploration ? 'demonstration' : 'exploration';
	const busy = playbackActive || Boolean(presentation && !presentation.settled(runtimeState));
	const episodeAvailable = Boolean(presentation && presentation.episodes.length > 0
		&& (presentation.canEnterEpisode?.(runtimeState) ?? true));

	const reduceAdapterIntent = (intent: SpaceOverlayIntent) => {
		setRuntimeState((current) => resolveRuntimeController({
			descriptor, config: runtimeConfig, runtimeState: current,
		}).reduceOverlayIntent?.({
			config: runtimeConfig, runtimeState: current, intent, cosmosData,
		}) ?? current);
	};
	const togglePlaybackSuspension = () => {
		const reduceSuspension = runtimeController.reducePlaybackSuspension;
		if (!reduceSuspension) throw new Error('Playback controls require adapter suspension support.');
		const suspended = !animationSuspendedRef.current;
		const resumeState = playbackCheckpoint?.runtimeState ?? runtimeState;
		animationSuspendedRef.current = suspended;
		setPlaybackCheckpoint(suspended ? { runtimeState } : undefined);
		let next = reduceSuspension({ runtimeState, suspended, resumeState });
		if (!suspended && suspendedCompletionRef.current) {
			suspendedCompletionRef.current = false;
			next = runtimeController.reduceDerivationTransitionComplete?.({
				config: runtimeConfig, runtimeState: next, cosmosData,
			}) ?? next;
		}
		setRuntimeState(next);
		setAnimationSuspended(suspended);
	};
	const resetPlayback = () => {
		setFreeCameraState(false);
		playbackGenerationRef.current += 1;
		setPlaybackResetKey(playbackGenerationRef.current);
		animationSuspendedRef.current = false;
		setAnimationSuspended(false);
		setPlaybackCheckpoint(undefined);
		suspendedCompletionRef.current = false;
	};
	/** Exploration only; a paused demonstration releases the camera through its suspension instead. */
	const setFreeCamera = (checked: boolean) => {
		if (mode === 'exploration') setFreeCameraState(checked);
	};
	const playbackGeneration = playbackResetKey;
	const onDerivationTransitionPhaseComplete = (transitionKey: string) => {
		if (playbackGeneration !== playbackGenerationRef.current || animationSuspendedRef.current) return;
		setRuntimeState((current) => resolveRuntimeController({
			descriptor, config: runtimeConfig, runtimeState: current,
		}).reduceDerivationTransitionPhaseComplete?.({
			config: runtimeConfig, runtimeState: current, cosmosData, transitionKey,
		}) ?? current);
	};
	const onDerivationTransitionComplete = () => {
		if (playbackGeneration !== playbackGenerationRef.current) return;
		if (animationSuspendedRef.current) {
			suspendedCompletionRef.current = true;
			return;
		}
		if (!runtimeController.reduceDerivationTransitionComplete) return;
		setRuntimeState((current) => resolveRuntimeController({
			descriptor, config: runtimeConfig, runtimeState: current,
		}).reduceDerivationTransitionComplete?.({
			config: runtimeConfig, runtimeState: current, cosmosData,
		}) ?? current);
	};
	const navigateEpisode = (handle?: string, interrupt = false) => {
		if (!interrupt && (playbackActive || (presentation && !presentation.settled(runtimeState)))) return;
		stopRuntimeEffects();
		if (handle && handle === episodeHandle) {
			setAppliedEpisode(undefined);
		}
		resetPlayback();
		navigateToEpisode(handle);
		setChoosingEpisode(false);
		setExploration(undefined);
	};
	const explore = () => {
		if (!presentation || !episodeHandle || !presentation.settled(runtimeState) || playbackActive) return;
		const checkpoint = presentation.pause(runtimeState);
		const detached = presentation.detach(checkpoint);
		stopRuntimeEffects();
		setExploration({ handle: episodeHandle, checkpoint, detached, confirmRestore: false });
		setRuntimeState(detached);
		animationSuspendedRef.current = false;
		setAnimationSuspended(false);
		setPlaybackCheckpoint(undefined);
		suspendedCompletionRef.current = false;
	};
	const resumeEpisode = (restoreConfirmed = false) => {
		if (!exploration || playbackActive || !presentation?.settled(runtimeState)) return;
		if (runtimeState !== exploration.detached && !restoreConfirmed) {
			setExploration({ ...exploration, confirmRestore: true });
			return;
		}
		setFreeCameraState(false);
		playbackGenerationRef.current += 1;
		setPlaybackResetKey(playbackGenerationRef.current);
		animationSuspendedRef.current = false;
		setAnimationSuspended(false);
		setPlaybackCheckpoint(undefined);
		suspendedCompletionRef.current = false;
		setRuntimeState(exploration.checkpoint);
		setExploration(undefined);
	};
	const selectMode = (value: SessionMode) => {
		if (choosingEpisode && value === 'exploration') { setChoosingEpisode(false); return; }
		if (value === mode) return;
		if (value === 'exploration') explore();
		else if (episodeAvailable) {
			if (exploration) resumeEpisode();
			else setChoosingEpisode(true);
		}
	};
	const keepExploring = () => {
		if (exploration) setExploration({ ...exploration, confirmRestore: false });
	};
	const prepareAdapterSelection = () => {
		setFreeCameraState(false);
		setChoosingEpisode(false);
		if (exploration) navigateEpisode();
	};

	const isAnimationSuspended = useCallback(() => animationSuspendedRef.current, []);
	const onRuntimeTick = useCallback((deltaMs: number) => {
		setRuntimeState((currentRuntimeState) => {
			const tickController = resolveRuntimeController({
				descriptor, config: runtimeConfig, runtimeState: currentRuntimeState,
			});
			return tickController.reduceRuntimeTick?.({
				config: runtimeConfig, runtimeState: currentRuntimeState, cosmosData, deltaMs,
			}) ?? currentRuntimeState;
		});
	}, [descriptor, cosmosData, runtimeConfig]);

	return {
		runtimeState, runtimeConfig, runtimeController, derivationState, routeError,
		mode, busy, episodeAvailable, choosingEpisode, confirmRestore: exploration?.confirmRestore ?? false,
		playbackActive, animationSuspended, playbackResetKey, freeCamera, setFreeCamera,
		selectMode, navigateEpisode, resumeEpisode, keepExploring, prepareAdapterSelection,
		togglePlaybackSuspension, resetPlayback, reduceAdapterIntent,
		onDerivationTransitionComplete, onDerivationTransitionPhaseComplete, onPlaybackActivityChange: setPlaybackActive,
		isAnimationSuspended, onRuntimeTick,
	};
}
